/**
 * Product Set Family Member hot-swap controller.
 * Scoped to [data-product-set-root]. Does not affect regular PDPs.
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-product-set-root]';
  var ACTIVE_SEL = '[data-product-set-active-content]';
  var TEMPLATE_SEL = '[data-product-set-template]';
  var MEMBER_RADIO_SEL = 'input[data-product-set-member]';
  var MEMBER_LABEL_SEL = '[data-product-set-member-label]';
  var BOUND = 'data-product-set-bound';

  var HERO_SEL = '[data-product-set-hero="true"]';
  var HERO_DISMISSED = 'data-product-set-hero-dismissed';

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qsa(root, sel) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
  }

  /**
   * Remove the one-time Product Set container hero from the live gallery.
   * After dismissal it must not return until a full page reload.
   */
  function removeSetHero(root) {
    if (!root || root.getAttribute(HERO_DISMISSED) === 'true') return false;

    var heroes = qsa(root, HERO_SEL);
    if (!heroes.length) {
      root.setAttribute(HERO_DISMISSED, 'true');
      return false;
    }

    var slideshow = qs(root, '.Product__Slideshow');
    var flkty = slideshow && window.Flickity ? Flickity.data(slideshow) : null;

    heroes.forEach(function (el) {
      if (flkty && el.classList.contains('Product__SlideItem')) {
        try {
          flkty.remove(el);
          return;
        } catch (e) {}
      }
      if (el.parentNode) el.parentNode.removeChild(el);
    });

    // Keep data-media-position aligned with Flickity / theme expectations (1-based).
    // Preserve originals so later alt-filtering can re-insert in gallery order.
    qsa(root, '.Product__Slideshow .Product__SlideItem').forEach(function (slide, index) {
      if (!slide.getAttribute('data-media-position-original')) {
        slide.setAttribute(
          'data-media-position-original',
          slide.getAttribute('data-media-position') || String(index + 1)
        );
      }
      slide.setAttribute('data-media-position', String(index + 1));
    });

    var slideCount = qsa(root, '.Product__Slideshow .Product__SlideItem').length;
    var mobileDots = qsa(root, '.Product__SlideshowMobileNav .flickity-page-dots .dot');
    while (mobileDots.length > slideCount) {
      var excess = mobileDots.shift();
      if (excess && excess.parentNode) excess.parentNode.removeChild(excess);
    }
    qsa(root, '.Product__SlideshowMobileNav .flickity-page-dots .dot').forEach(function (dot, index) {
      dot.setAttribute('data-index', String(index));
      dot.classList.toggle('is-selected', index === 0);
    });

    // Theme hides non-selected slides with Product__SlideItem--hidden. After removing the
    // selected hero those siblings stay hidden and the viewport looks blank — clear that.
    qsa(root, '.Product__Slideshow .Product__SlideItem').forEach(function (slide) {
      slide.classList.remove('Product__SlideItem--hidden');
      slide.style.removeProperty('display');
      slide.classList.remove('Product__SlideItem--mediaFilterHidden');
    });

    var firstSlide = qs(root, '.Product__Slideshow .Product__SlideItem');
    qsa(root, '.Product__SlideItem.is-selected').forEach(function (el) {
      el.classList.remove('is-selected');
    });
    if (firstSlide) firstSlide.classList.add('is-selected');

    var firstThumb = qs(root, '.Product__SlideshowNavImage:not([data-product-set-hero])');
    if (!firstThumb) firstThumb = qs(root, '.Product__SlideshowNavImage');
    qsa(root, '.Product__SlideshowNavImage.is-selected').forEach(function (el) {
      el.classList.remove('is-selected');
    });
    if (firstThumb) {
      firstThumb.classList.add('is-selected');
      firstThumb.classList.add('Product__SlideshowNavImage--show');
    }

    var firstDot = qs(root, '.Product__SlideshowNav--dots .Product__SlideshowNavDot');
    qsa(root, '.Product__SlideshowNav--dots .Product__SlideshowNavDot.is-selected').forEach(function (el) {
      el.classList.remove('is-selected');
    });
    if (firstDot) firstDot.classList.add('is-selected');

    if (flkty) {
      try {
        flkty.reloadCells();
        if (flkty.cells && flkty.cells.length) {
          flkty.select(0, true, true);
        }
        flkty.resize();
        if (typeof flkty.reposition === 'function') flkty.reposition();
      } catch (e2) {}
    }

    root.setAttribute(HERO_DISMISSED, 'true');
    document.dispatchEvent(new CustomEvent('product-set:hero-dismissed', {
      bubbles: true,
      detail: { root: root }
    }));
    return true;
  }

  function shouldDismissHeroFromTarget(target) {
    if (!target || !target.closest) return false;
    if (target.matches && target.matches(MEMBER_RADIO_SEL)) return true;
    if (target.matches && target.matches('input[name^="option-"], select[name="id"], input[name="quantity"]')) {
      return true;
    }
    if (target.closest('[data-product-set-member-label], .SizeSwatch, .ColorSwatch, .ProductForm__Option label, .QuantitySelector')) {
      // Ignore size-guide / measurement links inside option labels.
      if (target.closest('a[href], button[data-action="open-modal"], #size-guide-link, #measurements-link')) {
        return false;
      }
      return true;
    }
    return false;
  }

  function parseProductJson(scope) {
    var el = qs(scope, '[data-product-set-product-json], [data-product-json]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[ProductSet] Invalid product JSON', e);
      return null;
    }
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      var format = (window.theme && (window.theme.moneyFormat || window.theme.moneyWithCurrencyFormat)) ||
        (window.Shopify.currency && window.Shopify.currency.active ? '{{amount}}' : '{{amount}}');
      try {
        return Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || format);
      } catch (e) {}
    }
    var value = (Number(cents) || 0) / 100;
    return '$' + value.toFixed(2);
  }

  function getSelectedOptions(form) {
    var selected = {};
    qsa(form, 'input[type="radio"][name^="option-"]:checked').forEach(function (radio) {
      var pos = parseInt(radio.getAttribute('data-option-position'), 10);
      if (!pos || isNaN(pos)) {
        var match = (radio.getAttribute('name') || '').match(/^option-(\d+)/);
        if (match) pos = parseInt(match[1], 10) + 1;
      }
      if (pos) selected[pos] = radio.value;
    });
    return selected;
  }

  function findVariant(product, selected) {
    if (!product || !product.variants) return null;
    var variants = product.variants;
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var ok = true;
      for (var pos = 1; pos <= 3; pos++) {
        if (selected[pos] == null) continue;
        if (String(variant['option' + pos] || '') !== String(selected[pos])) {
          ok = false;
          break;
        }
      }
      if (ok) return variant;
    }
    return variants[0] || null;
  }

  function updatePrice(scope, variant) {
    if (!variant) return;
    var priceList = qs(scope, '.ProductMeta__PriceList');
    if (!priceList) return;

    var compare = variant.compare_at_price;
    var price = variant.price;
    if (window.SparkIg && variant.id) {
      price = window.SparkIg.priceCents(variant.id, price);
      compare = window.SparkIg.compareCents(variant.id, compare);
    }
    var productId = priceList.getAttribute('data-product-id');
    priceList.setAttribute('data-variant-id', variant.id);
    var idAttrs = (productId ? ' data-product-id="' + productId + '"' : '') + ' data-variant-id="' + variant.id + '"';
    var onSale = compare && compare > price;
    var onSaleLabel = priceList.getAttribute('data-on-sale-label') || 'On sale';
    var html = '';

    if (onSale) {
      html += '<span class="ProductMeta__Price Price Price--highlight Text--subdued u-h4"' + idAttrs + '>' + formatMoney(price) + '</span>';
      html += '<span class="ProductMeta__Price Price Price--compareAt Text--subdued u-h4"' + idAttrs + '>' + formatMoney(compare) + '</span>';
      html += '<span class="ProductMeta__Sale">' + onSaleLabel + '</span>';
    } else {
      html += '<span class="ProductMeta__Price Price Text--subdued u-h4"' + idAttrs + '>' + formatMoney(price) + '</span>';
    }
    priceList.innerHTML = html;

    var addBtn = qs(scope, '.ProductForm__AddToCart');
    if (addBtn && addBtn.querySelector('.Button__SeparatorDot')) {
      var spans = addBtn.querySelectorAll('span');
      if (spans.length >= 3) {
        var priceSpan = spans[spans.length - 1];
        priceSpan.textContent = formatMoney(price);
        priceSpan.setAttribute('data-variant-id', variant.id);
        if (productId) priceSpan.setAttribute('data-product-id', productId);
      }
    }
  }

  function updateAddToCart(scope, variant, productData) {
    var addBtn = qs(scope, '.ProductForm__AddToCart');
    var idInput = qs(scope, 'form.ProductForm [name="id"]');
    if (idInput && variant) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (!addBtn || !variant) return;

    var available = variant.available !== false;
    if (available) {
      addBtn.removeAttribute('disabled');
      // Must not use theme's add-to-cart action — that triggers drawer via product:added.
      addBtn.setAttribute('data-action', 'product-set-add-to-cart');
      var label = (window.languages && window.languages.productFormAddToCart) || 'Add to cart';
      var firstSpan = addBtn.querySelector('span');
      if (firstSpan && !firstSpan.classList.contains('Button__SeparatorDot')) {
        firstSpan.textContent = label;
      } else if (!addBtn.querySelector('span')) {
        addBtn.textContent = label;
      }
      addBtn.classList.remove('Button--secondary');
      addBtn.classList.add('Button--primary');
    } else {
      addBtn.setAttribute('disabled', 'disabled');
      addBtn.removeAttribute('data-action');
      var soldOut = (window.languages && window.languages.productFormSoldOut) || 'Sold out';
      addBtn.textContent = soldOut;
      addBtn.classList.add('Button--secondary');
      addBtn.classList.remove('Button--primary');
    }

    updatePrice(scope, variant);

    var skuEl = qs(scope, '.ProductMeta__SkuNumber');
    if (skuEl) skuEl.textContent = variant.sku || '';

    if (productData && productData.inventories && productData.inventories[variant.id]) {
      var inv = productData.inventories[variant.id];
      var invEl = qs(scope, '.ProductForm__Inventory');
      if (invEl) {
        invEl.textContent = inv.inventory_message || '';
      }
    }
  }

  function syncVariantIdOnly(scope) {
    var form = qs(scope, 'form.ProductForm');
    if (!form) return;
    var productData = parseProductJson(scope);
    if (!productData || !productData.product) return;

    var selected = getSelectedOptions(form);
    var variant = findVariant(productData.product, selected);
    if (!variant && productData.selected_variant_id) {
      variant = (productData.product.variants || []).find(function (v) {
        return String(v.id) === String(productData.selected_variant_id);
      }) || productData.product.variants[0];
    }
    if (!variant) return;

    var idInput = qs(scope, 'form.ProductForm [name="id"]');
    if (idInput) {
      idInput.value = variant.id;
    }
  }

  function bindVariantPicker(scope) {
    var form = qs(scope, 'form.ProductForm');
    if (!form || form.getAttribute('data-product-set-variant-bound') === 'true') return;
    form.setAttribute('data-product-set-variant-bound', 'true');

    var productData = parseProductJson(scope);
    if (!productData || !productData.product) return;

    var product = productData.product;

    function syncFromForm() {
      var selected = getSelectedOptions(form);
      var variant = findVariant(product, selected);
      if (!variant && productData.selected_variant_id) {
        variant = (product.variants || []).find(function (v) {
          return String(v.id) === String(productData.selected_variant_id);
        }) || product.variants[0];
      }
      // Price / ATC / variant id only. Gallery media is owned by custom.js alt-text
      // filtering + theme variant:changed (do not jump to featured_media here).
      updateAddToCart(scope, variant, productData);
    }

    form.addEventListener('change', function (event) {
      if (!event.target.matches('input[type="radio"][name^="option-"]')) return;
      syncFromForm();
    });

    // Do not rebuild/disable the server-rendered ATC button on init.
    // Only keep the hidden variant id aligned with the current selection.
    syncVariantIdOnly(scope);
  }

  function validateClone(node) {
    if (!node) return false;
    var form = qs(node, 'form.ProductForm');
    var json = parseProductJson(node);
    var idInput = qs(node, 'form.ProductForm [name="id"]');
    if (!form || !json || !json.product) return false;
    if (!idInput || !idInput.value) return false;
    return true;
  }

  function setMemberUi(root, member) {
    qsa(root, MEMBER_RADIO_SEL).forEach(function (radio) {
      var isActive = radio.getAttribute('data-product-set-member') === member;
      radio.checked = isActive;
    });

    qsa(root, MEMBER_LABEL_SEL).forEach(function (label) {
      var isActive = label.getAttribute('data-product-set-member-label') === member;
      label.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    var activeRadio = qs(root, MEMBER_RADIO_SEL + '[data-product-set-member="' + member + '"]');
    if (activeRadio) {
      var labelText = activeRadio.getAttribute('data-member-label') || activeRadio.value;
      qsa(root, '[data-product-set-selected-label]').forEach(function (el) {
        el.textContent = labelText;
      });
    }

    root.setAttribute('data-active-member', member);
    var active = qs(root, ACTIVE_SEL);
    var json = parseProductJson(active);
    if (json && json.product) {
      root.setAttribute('data-displayed-product-id', json.product.id);
    }
  }

  function setSwitching(root, on) {
    root.classList.toggle('is-switching', !!on);
    qsa(root, MEMBER_RADIO_SEL).forEach(function (radio) {
      radio.disabled = !!on;
    });
  }

  /**
   * Destroy theme ProductSection for this root while old DOM is still attached,
   * then recreate it after new content is inserted. Uses the theme's real
   * shopify:section:unload / shopify:section:load lifecycle (SectionContainer).
   */
  function unloadProductSection(root) {
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    document.dispatchEvent(new CustomEvent('shopify:section:unload', {
      bubbles: true,
      detail: { sectionId: sectionId }
    }));
  }

  function loadProductSection(root) {
    var parent = root.parentNode;
    if (!parent) return false;

    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return false;

    // SectionContainer._onSectionLoad looks for a descendant [data-section-id].
    // detail.sectionId is required: other page listeners (e.g. UGC slider) read it
    // and throw if event.detail is null.
    var host = document.createElement('div');
    host.setAttribute('data-product-set-section-host', '');
    host.style.display = 'contents';
    parent.insertBefore(host, root);
    host.appendChild(root);

    try {
      host.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: sectionId }
      }));
    } catch (e) {
      console.warn('[ProductSet] ProductSection reload failed', e);
      parent.insertBefore(root, host);
      if (host.parentNode) host.parentNode.removeChild(host);
      return false;
    }

    parent.insertBefore(root, host);
    if (host.parentNode) host.parentNode.removeChild(host);
    return true;
  }

  function refreshGalleryLayout(active) {
    if (!active || !window.Flickity) return;
    var slideshow = qs(active, '.Product__Slideshow');
    if (!slideshow) return;
    var flkty = Flickity.data(slideshow);
    if (!flkty || !flkty.isActive) return;

    requestAnimationFrame(function () {
      try {
        flkty.resize();
      } catch (e) {}
    });
  }

  function switchMember(root, member) {
    if (!root || root.getAttribute('data-active-member') === member) {
      setMemberUi(root, member);
      return;
    }

    // Templates omit the container hero; mark dismissed so filters never restore it.
    removeSetHero(root);

    var previousMember = root.getAttribute('data-active-member') || 'dog';
    var template = qs(root, TEMPLATE_SEL + '[data-product-set-template="' + member + '"]');
    if (!template) {
      console.warn('[ProductSet] Missing template for member:', member);
      setMemberUi(root, previousMember);
      return;
    }

    var clone = template.content.cloneNode(true);
    var wrapper = document.createElement('div');
    wrapper.appendChild(clone);

    if (!validateClone(wrapper)) {
      console.warn('[ProductSet] Invalid template content for member:', member);
      setMemberUi(root, previousMember);
      return;
    }

    var active = qs(root, ACTIVE_SEL);
    if (!active) return;

    setSwitching(root, true);

    var previousHtml = active.innerHTML;
    var inserted = false;
    var reloaded = false;

    try {
      // 1–3: validate done; disable done; destroy current Product Set gallery / section
      unloadProductSection(root);

      // 4–5: replace and keep content attached/visible
      active.innerHTML = '';
      while (wrapper.firstChild) {
        active.appendChild(wrapper.firstChild);
      }
      inserted = true;

      // 6–11: re-init ProductSection (Carousel/Flickity, thumbs, mobile nav, zoom, variants)
      reloaded = loadProductSection(root);
      if (!reloaded) {
        throw new Error('ProductSection failed to reinitialize');
      }

      var gallery = qs(active, '.Product__Slideshow, .Product__Gallery');
      if (!gallery && qs(active, '.Product__Info--noGallery') == null) {
        // Product may legitimately have no media; only fail if gallery was expected.
        var json = parseProductJson(active);
        if (json && json.product && json.product.media && json.product.media.length) {
          throw new Error('Media gallery missing after insert');
        }
      }

      setMemberUi(root, member);
      bindVariantPicker(active);

      if (window.Shopify && Shopify.PaymentButton && typeof Shopify.PaymentButton.init === 'function') {
        try { Shopify.PaymentButton.init(); } catch (e) {}
      }

      document.dispatchEvent(new CustomEvent('product-set:content-replaced', {
        detail: { root: root, member: member, active: active }
      }));

      if (window.ProductSetSizeGuide && typeof window.ProductSetSizeGuide.sync === 'function') {
        try { window.ProductSetSizeGuide.sync(member); } catch (e) {}
      }

      // 12: recalculate Flickity after layout
      refreshGalleryLayout(active);
    } catch (err) {
      console.warn('[ProductSet] Family member switch failed; restoring previous content.', err);

      if (inserted) {
        active.innerHTML = previousHtml;
        try {
          loadProductSection(root);
        } catch (e2) {
          console.warn('[ProductSet] Failed to restore ProductSection', e2);
        }
      } else {
        // Unload happened but insert did not — restore previous markup then reload.
        active.innerHTML = previousHtml;
        try {
          loadProductSection(root);
        } catch (e3) {
          console.warn('[ProductSet] Failed to restore ProductSection', e3);
        }
      }

      setMemberUi(root, previousMember);
      bindVariantPicker(active);
      refreshGalleryLayout(active);
    }

    setSwitching(root, false);
  }

  function onRootChange(event) {
    var target = event.target;
    if (!target) return;

    var root = target.closest ? target.closest(ROOT_SEL) : null;
    if (!root) return;

    // Only real user gestures dismiss the Set hero — ignore synthetic/init events.
    if (event.isTrusted && shouldDismissHeroFromTarget(target)) {
      removeSetHero(root);
    }

    if (!target.matches || !target.matches(MEMBER_RADIO_SEL)) return;

    // Prevent theme ProductVariants from treating Family Member as a product option.
    event.stopPropagation();

    var member = target.getAttribute('data-product-set-member');
    if (!member) return;
    switchMember(root, member);
  }

  function onRootPointerDown(event) {
    // Programmatic .click() during gallery init is not trusted and must not dismiss the hero.
    if (!event.isTrusted) return;
    var target = event.target;
    if (!shouldDismissHeroFromTarget(target)) return;
    var root = target.closest ? target.closest(ROOT_SEL) : null;
    if (root) removeSetHero(root);
  }

  function bindRoot(root) {
    if (!root || root.getAttribute(BOUND) === 'true') return;
    root.setAttribute(BOUND, 'true');

    // Capture phase so ProductVariants (bubble on same root) never sees these radios.
    root.addEventListener('change', onRootChange, true);
    root.addEventListener('click', onRootPointerDown, true);

    var active = qs(root, ACTIVE_SEL);
    if (active) bindVariantPicker(active);

    // Ensure server-rendered Dog selected UI is reflected on labels.
    setMemberUi(root, root.getAttribute('data-active-member') || 'dog');
  }

  function initAll() {
    qsa(document, ROOT_SEL).forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    // Ignore our own temporary host used during Product Set gallery reload.
    if (event.target && event.target.getAttribute && event.target.getAttribute('data-product-set-section-host') !== null) {
      return;
    }

    var root = event.target && event.target.querySelector
      ? (event.target.matches(ROOT_SEL) ? event.target : event.target.querySelector(ROOT_SEL))
      : null;
    if (root) {
      root.removeAttribute(BOUND);
      bindRoot(root);
    } else {
      initAll();
    }
  });
})();
